import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ShoppingBag, Truck, Ruler, Stamp, X, ChevronLeft, ChevronRight, ZoomIn, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCart } from "@/lib/cart";
import SEO, { productSchema, breadcrumbSchema } from "@/components/SEO";
import { SquigglyUnderline } from "@/components/Doodle";
import PersonalisationModal from "@/components/PersonalisationModal";

export default function Product() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [size, setSize] = useState("");
  const [qty, setQty] = useState(1);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [magnify, setMagnify] = useState({ x: 50, y: 50, active: false });
  const [adding, setAdding] = useState(false);
  const [personalisable, setPersonalisable] = useState(null); // null = not personalisable
  const [personalisationOpen, setPersonalisationOpen] = useState(false);
  const touchStartX = useRef(null);
  const { addItem } = useCart();

  useEffect(() => {
    api
      .get(`/products/${id}`)
      .then((r) => {
        setProduct(r.data);
        setSize(r.data.sizes?.[1] || r.data.sizes?.[0] || "");
        setActiveImg(0);
      })
      .catch(() => setNotFound(true));
    // Check whether this Shopify product has been marked personalisable
    api
      .get(`/personalisable-products/${id}`)
      .then((r) => setPersonalisable(r.data))
      .catch(() => setPersonalisable(null));
  }, [id]);

  const allImages = product
    ? [product.image, ...(product.extra_images || [])].filter(Boolean)
    : [];

  const next = useCallback(
    () => setActiveImg((i) => (i + 1) % Math.max(1, allImages.length)),
    [allImages.length]
  );
  const prev = useCallback(
    () =>
      setActiveImg(
        (i) => (i - 1 + Math.max(1, allImages.length)) % Math.max(1, allImages.length)
      ),
    [allImages.length]
  );

  // Keyboard nav for lightbox
  useEffect(() => {
    if (!zoomOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setZoomOpen(false);
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [zoomOpen, next, prev]);

  // Desktop hover-magnify on main product image
  const onImgMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * 100;
    const y = ((e.clientY - r.top) / r.height) * 100;
    setMagnify({ x, y, active: true });
  };
  const onImgMouseLeave = () =>
    setMagnify((m) => ({ ...m, active: false }));

  // Swipe gesture inside lightbox (mobile)
  const onLightboxTouchStart = (e) => {
    touchStartX.current = e.targetTouches[0].clientX;
  };
  const onLightboxTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (dx > 60) prev();
    else if (dx < -60) next();
    touchStartX.current = null;
  };

  if (notFound) {
    return (
      <div className="mx-auto max-w-[800px] px-6 py-32 text-center md:px-10">
        <h1 className="font-display text-5xl font-black">Product not found.</h1>
        <p className="mt-4">It was too damp. It dissolved.</p>
        <Link to="/shop" className="wc-btn mt-8">
          Back to shop
        </Link>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="mx-auto max-w-[1400px] px-6 py-20 md:px-10">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2">
          <div className="aspect-[4/5] animate-pulse border-2 border-[#1A3626] bg-[#EBE7DD]" />
          <div className="space-y-4">
            <div className="h-8 w-1/3 animate-pulse bg-[#EBE7DD]" />
            <div className="h-14 w-3/4 animate-pulse bg-[#EBE7DD]" />
            <div className="h-24 animate-pulse bg-[#EBE7DD]" />
          </div>
        </div>
      </div>
    );
  }

  const addToCart = async ({ personalised = false } = {}) => {
    if (product.sizes?.length && !size) {
      toast.error("Pick a size first (it matters).");
      return;
    }
    // Find matching Shopify variant for the selected size
    const variants = product.variants || [];
    let variant = null;
    if (size) {
      variant = variants.find(
        (v) => (v.options?.size || "").toLowerCase() === size.toLowerCase()
      );
    }
    if (!variant) variant = variants[0];

    if (!variant) {
      toast.success(
        `${product.name} (${size || "one size"}) added — Shopify not configured.`,
        { duration: 3000 }
      );
      return;
    }

    let attributes = null;
    if (personalised) {
      attributes = [
        { key: "Personalisation", value: "Custom photo upload" },
      ];
    }

    setAdding(true);
    try {
      await addItem({
        variantId: variant.id,
        quantity: qty,
        attributes,
      });
      toast.success(
        personalised ? "Personalised tee added 🥬✨" : "Added to cart 🥬",
        { duration: 1500 }
      );
    } catch (e) {
      toast.error("Couldn't add that. Try again?");
    } finally {
      setAdding(false);
    }
  };

  return (
    <div data-testid="product-page">
      <SEO
        title={product.name}
        path={`/shop/${product.id}`}
        description={
          (product.description || product.tagline || "").slice(0, 160) ||
          `${product.name} — Wet Cabbage premium streetwear.`
        }
        image={product.image}
        type="product"
        schema={{
          "@context": "https://schema.org",
          "@graph": [
            productSchema(product),
            breadcrumbSchema([
              { name: "Shop", path: "/shop" },
              { name: product.name, path: `/shop/${product.id}` },
            ]),
          ],
        }}
      />
      <div className="mx-auto max-w-[1400px] px-6 py-10 md:px-10">
        <Link
          to="/shop"
          className="font-display inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest hover:underline"
          data-testid="back-to-shop"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={2.5} /> Back to shop
        </Link>
      </div>

      <section className="pb-20 md:pb-32">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-10 px-6 md:grid-cols-2 md:px-10">
          <div className="md:sticky md:top-24 md:self-start">
            <button
              type="button"
              onClick={() => setZoomOpen(true)}
              onMouseMove={onImgMouseMove}
              onMouseLeave={onImgMouseLeave}
              className="group relative block w-full overflow-hidden border-2 border-[#1A3626] bg-[#EBE7DD]"
              aria-label="Zoom image"
              data-testid="product-zoom-trigger"
            >
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src={allImages[activeImg] || product.image}
                  alt={product.name}
                  className="h-full w-full object-cover transition-transform duration-200 ease-out"
                  style={{
                    transformOrigin: `${magnify.x}% ${magnify.y}%`,
                    transform: magnify.active ? "scale(2.2)" : "scale(1)",
                  }}
                  data-testid="product-hero-image"
                />
              </div>
              <span className="font-display absolute bottom-3 right-3 inline-flex items-center gap-1 border-2 border-[#1A3626] bg-[#F9F9F4] px-2 py-1 text-[10px] font-bold uppercase tracking-widest opacity-0 transition-opacity group-hover:opacity-100 md:flex">
                <ZoomIn className="h-3 w-3" strokeWidth={2.8} /> Move to magnify · click for fullscreen
              </span>
              <span
                className="font-display pointer-events-none absolute bottom-3 right-3 inline-flex items-center gap-1 border-2 border-[#1A3626] bg-[#F9F9F4] px-2 py-1 text-[10px] font-bold uppercase tracking-widest md:hidden"
                aria-hidden
              >
                <ZoomIn className="h-3 w-3" strokeWidth={2.8} /> Tap to zoom
              </span>
            </button>

            {allImages.length > 1 && (
              <div
                className="mt-3 flex gap-3 overflow-x-auto"
                data-testid="product-thumbs"
              >
                {allImages.map((src, i) => (
                  <button
                    key={src + i}
                    type="button"
                    onClick={() => setActiveImg(i)}
                    className={`shrink-0 border-2 transition-all ${
                      i === activeImg
                        ? "border-[#1A3626]"
                        : "border-[#1A3626]/30 hover:border-[#1A3626]"
                    }`}
                    aria-label={`Image ${i + 1}`}
                    data-testid={`product-thumb-${i}`}
                  >
                    <img
                      src={src}
                      alt=""
                      className="h-20 w-20 object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="wc-stamp">
                <Stamp className="h-3.5 w-3.5" strokeWidth={3} />
                Designed For Modern Leisure
              </span>
              <span className="font-display border-2 border-[#1A3626] bg-[#F9F9F4] px-3 py-1 text-xs font-bold uppercase tracking-widest">
                {product.category}
              </span>
            </div>
            <h1
              className="font-display mt-6 text-4xl font-black leading-[0.95] tracking-tighter sm:text-6xl"
              data-testid="product-title"
            >
              {product.name}
            </h1>
            <div className="mt-2 max-w-md">
              <SquigglyUnderline className="h-3 w-full" />
            </div>

            <div className="mt-6 flex items-baseline gap-4">
              <div
                className="font-display text-3xl font-black"
                data-testid="product-price"
              >
                {product.currency === "USD" ? "$" : ""}
                {product.price.toFixed(2)}
                {product.currency && product.currency !== "USD" ? ` ${product.currency}` : ""}
              </div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#1A3626]">
                {product.currency || "USD"} · taxes at checkout
              </div>
            </div>

            <p className="mt-6 max-w-prose whitespace-pre-line text-base leading-relaxed md:text-lg">
              {product.description}
            </p>

            {product.artist && (
              <div className="mt-4 text-sm italic text-[#1A3626]">
                Artwork by <span className="font-bold">{product.artist}</span>
              </div>
            )}

            {product.sizes?.length > 0 && (
              <div className="mt-8">
                <div className="font-display mb-3 text-xs font-bold uppercase tracking-[0.3em]">
                  Size
                </div>
                <div className="flex flex-wrap gap-2">
                  {product.sizes.map((s) => (
                    <button
                      key={s}
                      onClick={() => setSize(s)}
                      className={`font-display min-w-[56px] border-2 border-[#1A3626] px-4 py-3 text-sm font-bold uppercase tracking-widest transition-all ${
                        size === s
                          ? "bg-[#1A3626] text-[#F9F9F4]"
                          : "bg-transparent hover:bg-[#EBE7DD]"
                      }`}
                      data-testid={`size-${s}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-6 flex items-center gap-4">
              <div className="font-display text-xs font-bold uppercase tracking-[0.3em]">
                Qty
              </div>
              <div className="flex items-stretch border-2 border-[#1A3626]">
                <button
                  onClick={() => setQty(Math.max(1, qty - 1))}
                  className="px-3 font-display font-black"
                  data-testid="qty-dec"
                  aria-label="decrease"
                >
                  −
                </button>
                <div className="min-w-[48px] border-x-2 border-[#1A3626] px-3 py-2 text-center font-display font-bold">
                  {qty}
                </div>
                <button
                  onClick={() => setQty(qty + 1)}
                  className="px-3 font-display font-black"
                  data-testid="qty-inc"
                  aria-label="increase"
                >
                  +
                </button>
              </div>
            </div>

            {personalisable && (
              <div className="mt-8 space-y-3" data-testid="personalisable-ctas">
                <button
                  onClick={() => setPersonalisationOpen(true)}
                  className="wc-btn flex w-full items-center justify-center gap-2"
                  data-testid="personalise-cta"
                >
                  <Sparkles className="h-4 w-4" strokeWidth={2.8} />
                  Personalise This Item
                </button>
                <button
                  onClick={() => addToCart({ personalised: false })}
                  disabled={adding}
                  className="font-display flex w-full items-center justify-center gap-2 border-2 border-[#1A3626] bg-transparent px-7 py-3.5 text-xs font-black uppercase tracking-[0.32em] text-[#1A3626] transition-all hover:bg-[#1A3626] hover:text-[#F9F9F4] disabled:opacity-60"
                  data-testid="add-to-cart-standard"
                >
                  <ShoppingBag className="h-4 w-4" strokeWidth={2.6} />
                  {adding ? "Adding…" : "Buy Standard Version"}
                </button>
                <p className="text-center text-[11px] uppercase tracking-[0.28em] text-[#1A3626]/65">
                  Hand-crafted proof · No payment until you approve it
                </p>
              </div>
            )}

            {!personalisable && (
              <button
                onClick={() => addToCart()}
                disabled={adding}
                className="wc-btn mt-8 w-full disabled:opacity-60 md:w-auto"
                data-testid="add-to-cart"
              >
                <ShoppingBag className="mr-2 h-4 w-4" strokeWidth={2.8} />
                {adding ? "Adding…" : "Add to cart"}
              </button>
            )}

            <div className="mt-8 grid grid-cols-1 gap-3 border-t-2 border-[#1A3626] pt-6 text-sm md:grid-cols-2">
              <div className="flex items-start gap-2">
                <Truck className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={2.5} />
                <span>
                  Printed &amp; shipped on demand via our weird little warehouse friends.
                </span>
              </div>
              <div className="flex items-start gap-2">
                <Ruler className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={2.5} />
                <span>
                  Runs true to size, but oversized is encouraged. More room for feelings.
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox / image zoom overlay */}
      {zoomOpen && allImages.length > 0 && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-[#0F0F0F]/95 p-4 backdrop-blur-sm"
          role="dialog"
          aria-label="Product image zoom"
          onClick={() => setZoomOpen(false)}
          onTouchStart={onLightboxTouchStart}
          onTouchEnd={onLightboxTouchEnd}
          data-testid="product-lightbox"
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setZoomOpen(false);
            }}
            className="font-display absolute right-4 top-4 inline-flex items-center gap-1 border-2 border-[#F9F9F4] bg-[#1A3626] px-3 py-2 text-xs font-bold uppercase tracking-widest text-[#F9F9F4] transition-all hover:bg-[#93C065] hover:text-[#0F0F0F]"
            aria-label="Close zoom"
            data-testid="lightbox-close"
          >
            <X className="h-4 w-4" strokeWidth={2.8} /> Close
          </button>

          {allImages.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  prev();
                }}
                className="absolute left-4 top-1/2 -translate-y-1/2 border-2 border-[#F9F9F4] bg-[#1A3626] p-3 text-[#F9F9F4] transition-all hover:bg-[#93C065] hover:text-[#0F0F0F]"
                aria-label="Previous image"
                data-testid="lightbox-prev"
              >
                <ChevronLeft className="h-6 w-6" strokeWidth={2.8} />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  next();
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 border-2 border-[#F9F9F4] bg-[#1A3626] p-3 text-[#F9F9F4] transition-all hover:bg-[#93C065] hover:text-[#0F0F0F]"
                aria-label="Next image"
                data-testid="lightbox-next"
              >
                <ChevronRight className="h-6 w-6" strokeWidth={2.8} />
              </button>
            </>
          )}

          <img
            src={allImages[activeImg]}
            alt={product.name}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[90vh] max-w-[92vw] border-2 border-[#F9F9F4] bg-[#F9F9F4] object-contain"
            style={{ touchAction: "pinch-zoom" }}
            data-testid="lightbox-image"
          />

          <div className="font-display pointer-events-none absolute bottom-16 left-1/2 hidden -translate-x-1/2 text-[10px] uppercase tracking-[0.25em] text-[#A2A98F] md:block">
            ← → arrows · esc to close
          </div>
          <div className="font-display pointer-events-none absolute bottom-16 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.25em] text-[#A2A98F] md:hidden">
            swipe · pinch to zoom · tap outside to close
          </div>

          {allImages.length > 1 && (
            <div className="font-display absolute bottom-4 left-1/2 -translate-x-1/2 border-2 border-[#F9F9F4] bg-[#1A3626] px-3 py-1 text-xs font-bold uppercase tracking-widest text-[#F9F9F4]">
              {activeImg + 1} / {allImages.length}
            </div>
          )}
        </div>
      )}

      {personalisable && (
        <PersonalisationModal
          open={personalisationOpen}
          onClose={() => setPersonalisationOpen(false)}
          personalisable={personalisable}
        />
      )}
    </div>
  );
}
